import { useEffect } from "react";
import { Calculator, FlaskConical } from "lucide-react";
import { COLS, ROWS } from "./JSchedule";
import { settleOnId } from "../../lib/api";
import { useTanks } from "../../lib/tanks";
import { JTanks } from "./JTanks";

const LABEL = (c) => (c.startsWith("C") ? `Crecimiento S${c.slice(1)}` : c === "F8" ? "Lavado" : `Floración S${c.slice(1)}`);
const num = (s) => parseFloat(String(s).replace(",", "."));
const fmt = (v) => (Math.round(v * 10) / 10).toLocaleString("es-ES", { maximumFractionDigits: 1 });
const ml = (dose, liters) => {
  const [a, b] = String(dose).split("–");
  return b ? `${fmt(num(a) * liters)}–${fmt(num(b) * liters)}` : fmt(num(a) * liters);
};

export const JMixCalc = ({ week: planWeek }) => {
  const tanksApi = useTanks();
  const { liters, plants } = tanksApi.tank;
  const week = tanksApi.tank.week ?? planWeek;
  const setWeek = (c) => tanksApi.update({ week: c });
  const setLiters = (v) => tanksApi.update({ liters: v });
  const setPlants = (v) => tanksApi.update({ plants: v });
  const L = Math.max(0, num(liters) || 0);
  const P = Math.max(0, parseInt(plants, 10) || 0);
  const rows = ROWS.filter(([, , , d]) => d?.[week]);

  useEffect(() => {
    if (window.location.hash !== "#calculadora") return undefined;
    return settleOnId("calculadora");
  }, []);

  return (
    <div id="calculadora" data-testid="mix-calculator" className="rounded-[10px] label-frame bg-[#0E2A1C] p-5 md:p-8 mb-8 grid lg:grid-cols-12 gap-8">
      <div className="lg:col-span-5">
        <p className="font-mono-d text-[11px] tracking-[0.22em] uppercase text-[#F2B441] flex items-center gap-2"><Calculator className="w-4 h-4" /> Calculadora de riego</p>
        <p className="font-serif-d font-black text-3xl md:text-4xl mt-3 leading-tight">¿Cuánto echo en mi depósito?</p>
        <JTanks {...tanksApi} planWeek={planWeek} />
        <div className="mt-4 grid grid-cols-2 gap-4">
          <label className="block">
            <span className="text-xs text-[#C9D6C2]">Litros de agua</span>
            <input data-testid="mix-liters-input" type="number" min="0" step="0.5" inputMode="decimal" value={liters} onChange={(e) => setLiters(e.target.value)}
              className="mt-1 w-full h-12 rounded-[6px] bg-[#143823] border border-[#F6EEDC]/25 px-3 font-mono-d text-lg text-[#F6EEDC] focus:border-[#F2B441] outline-none" />
          </label>
          <label className="block">
            <span className="text-xs text-[#C9D6C2]">Nº de plantas</span>
            <input data-testid="mix-plants-input" type="number" min="0" step="1" inputMode="numeric" value={plants} onChange={(e) => setPlants(e.target.value)}
              className="mt-1 w-full h-12 rounded-[6px] bg-[#143823] border border-[#F6EEDC]/25 px-3 font-mono-d text-lg text-[#F6EEDC] focus:border-[#F2B441] outline-none" />
          </label>
        </div>
        <p className="text-xs text-[#C9D6C2] mt-5 mb-2">Semana de cultivo de este depósito</p>
        <div className="flex flex-wrap gap-1.5" data-testid="mix-week-select">
          {COLS.map((c) => (
            <button key={c} onClick={() => setWeek(c)} data-testid={`mix-week-${c}`}
              className={`h-9 px-3 rounded-full text-xs font-mono-d border transition-colors ${week === c ? "bg-[#F2B441] border-[#F2B441] text-[#0E2A1C] font-bold" : "border-[#F6EEDC]/25 text-[#E3EADF] hover:border-[#F2B441]"}`}>
              {c}
            </button>
          ))}
        </div>
      </div>
      <div className="lg:col-span-7">
        <p className="font-mono-d text-[11px] tracking-[0.22em] uppercase text-[#8CD05A]">{LABEL(week)} · {fmt(L)} L{P ? ` · ${fmt(L / P)} L por planta` : ""}</p>
        <ul data-testid="mix-results" className="mt-4 space-y-2">
          {rows.map(([id, name, img, d]) => (
            <li key={id} data-testid={`mix-result-${id}`} className="flex items-center gap-4 rounded-[8px] bg-[#143823] border border-[#F6EEDC]/10 p-3">
              <img src={img} alt="" className="w-12 h-12 object-contain" />
              <div className="flex-1">
                <p className="font-label text-base">{name}</p>
                <p className="font-mono-d text-[11px] text-[#C9D6C2]">{d[week]} mL/L · {ml(d[week], 100)} ml por 100 L</p>
              </div>
              <p className="font-serif-d font-black text-2xl md:text-3xl text-[#F2B441] whitespace-nowrap">{ml(d[week], L)} <span className="text-sm font-mono-d text-[#E3EADF]">mL</span></p>
            </li>
          ))}
          {rows.length === 0 && <li data-testid="mix-result-water" className="rounded-[8px] bg-[#5B7FE0]/15 border border-[#5B7FE0]/40 p-4 text-sm">Semana de lavado: riega solo con agua, sin nutrientes.</li>}
        </ul>
        <p className="text-xs text-[#C9D6C2] mt-4 flex gap-2"><FlaskConical className="w-4 h-4 shrink-0 text-[#8CD05A]" /> Agita los envases, añade los productos de uno en uno en el orden de la lista y mezcla hasta disolver. La solución ajusta su pH sola (Efecto Buffer); revisa la EC.</p>
      </div>
    </div>
  );
};
